import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { fetchData } from "../../../helpers/fetchData";
import CustomTable from "../../../shared/CustomTable";
import {
  FaCalendarCheck,
  FaStethoscope,
  FaNotesMedical,
  FaFileInvoiceDollar,
  FaFlask,
  FaXRay,
  FaPills,
  FaClock,
} from "react-icons/fa";
import { MdOutlineHourglassEmpty } from "react-icons/md";

const EVENT_CONFIG = {
  appointment: { color: "#0288D1", icon: FaCalendarCheck },
  visit:       { color: "#0070C2", icon: FaStethoscope },
  emr:         { color: "#9333EA", icon: FaNotesMedical },
  invoice:     { color: "#01B051", icon: FaFileInvoiceDollar },
  lab:         { color: "#F97316", icon: FaFlask },
  radiology:   { color: "#0D9488", icon: FaXRay },
  pharmacy:    { color: "#2E7D32", icon: FaPills },
  other:       { color: "#9CA3AF", icon: FaClock },
};

const PatientJourneyTimeline = ({ patientId, fileNumber, documentId, visitId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ totalEvents: 0, journeyDuration: "", startedOn: "", completedOn: "", patientName: "", patientFileNo: "", patientDocId: "" });

  const fetchJourney = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (patientId) payload.patient_id = patientId;
      if (fileNumber) payload.file_number = fileNumber;
      if (documentId) payload.document_id = documentId;
      if (visitId) payload.visit_id = visitId;
      const response = await fetchData("POST", "/v3/patientHistory/journey-timeline", payload);
      if (response?.events?.length > 0) {
        const list = response.events;
        const first = list[0];
        const last = list[list.length - 1];
        setEvents(list);
        setStats({
          totalEvents: response.total_events || list.length,
          patientName: response.patient_name || first?.patient_name || "",
          patientFileNo: response.file_number || first?.file_number || "",
          patientDocId: response.patient_document_id || first?.patient_document_id || "",
          startedOn: moment(first.created_at).format("DD MMM YYYY, hh:mm A"),
          completedOn: moment(last.created_at).format("DD MMM YYYY, hh:mm A"),
          journeyDuration: formatDuration(moment(last.created_at).diff(moment(first.created_at))),
        });
      } else { setEvents([]); }
    } catch (e) { console.error(e); setEvents([]); }
    finally { setLoading(false); }
  }, [patientId, fileNumber, documentId]);

  useEffect(() => { fetchJourney(); }, [fetchJourney]);

  const cfg = (t) => EVENT_CONFIG[t] || EVENT_CONFIG.other;

  const getInitials = (n) => {
    if (!n) return "?";
    return n.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <span className="text-sm text-gray-400 font-medium">{t("Loading journey...")}</span>
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-14 h-14 rounded-xl bg-gray-50 flex items-center justify-center mb-3">
          <MdOutlineHourglassEmpty size={24} className="text-gray-300" />
        </div>
        <p className="text-sm font-semibold text-gray-500">{t("No journey events found")}</p>
        <p className="text-xs text-gray-400 mt-1">{t("This patient has no timeline data yet")}</p>
      </div>
    );
  }

  const columns = [
    {
      header: "#",
      body: (rowData, { rowIndex }) => {
        const conf = cfg(rowData.event_type);
        const Icon = conf.icon;
        const isLast = rowIndex === events.length - 1;
        return (
          <div className="flex items-center justify-start relative min-h-[48px] pl-2">
            {!isLast && (
              <div className="absolute top-[28px] bottom-[-24px] left-[16px] w-[2px] bg-gray-200 z-0" />
            )}
            <div
              className="relative z-10 w-7 h-7 flex items-center justify-center rounded-full text-white shadow-sm"
              style={{ backgroundColor: conf.color }}
            >
              <Icon size={11} />
            </div>
          </div>
        );
      },
      style: { width: "50px", textAlign: "left", padding: "0" },
      bodyCellClass: "!border-b-0 !px-0",
    },
    {
      header: t("Activity"),
      body: (rowData) => (
        <div>
          <span className="text-14-dark font-semibold">{rowData.action_reason || rowData.event_label || "—"}</span>
          {rowData.lab_order_status ? (
            <span className="ml-2 text-[11px] bg-orange-50 text-orange-600 px-2 py-0.5 rounded font-medium">{rowData.lab_order_status}</span>
          ) : null}
          {rowData.radiology_order_status ? (
            <span className="ml-2 text-[11px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded font-medium">{rowData.radiology_order_status}</span>
          ) : null}
          {rowData.pharmacy_invoice_number ? (
            <span className="ml-2 text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-medium">{t("Pharmacy")}</span>
          ) : null}
          {rowData.emr_id ? (
            <span className="ml-2 text-[11px] bg-purple-50 text-purple-600 px-2 py-0.5 rounded font-medium">{t("EMR")} #{rowData.emr_id}</span>
          ) : null}
        </div>
      ),
    },
    {
      header: t("Performed By"),
      body: (rowData) =>
        rowData.action_performed_by?.name
          ? `${rowData.action_performed_by.name}${rowData.action_performed_by.role ? ` (${rowData.action_performed_by.role})` : ""}`
          : "—",
    },
    {
      header: t("Date & Time"),
      body: (rowData) => (
        <span className="text-12-gray">{moment(rowData.created_at).format("DD MMM YYYY, hh:mm A")}</span>
      ),
    },
    {
      header: t("Wait"),
      body: (rowData, { rowIndex }) => {
        const wait = rowData.duration_from_previous_formatted;
        if (rowIndex === 0 || wait === "-") {
          return <span className="text-gray-300 text-sm">—</span>;
        }
        return (
          <span className="flex items-center w-[80px] justify-center gap-1.5 text-primary font-semibold bg-primary/5 px-3 py-1 rounded-full text-[12px]">
            <FaClock size={10} />{wait}
          </span>
        );
      },
      style: { textAlign: "center" },
      bodyCellClass: "text-center",
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between flex-wrap gap-4 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-xl px-5 py-4">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-[15px] font-bold text-darkBg">{stats.patientName}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {t("Pt")} {patientId || "—"}  ·  {t("File")} {stats.patientFileNo || "—"}  ·  {t("Doc")} {stats.patientDocId || "—"}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <MiniStat label={t("Events")} value={stats.totalEvents} />
          <MiniStat label={t("Duration")} value={stats.journeyDuration} />
          <MiniStat label={t("Started")} value={stats.startedOn} />
          <MiniStat label={t("Completed")} value={stats.completedOn} />
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <CustomTable
        data={events}
        columns={columns}
        emptyMessage={t("No records found")}
      />

      <p className="text-center text-[11px] text-gray-400">
        {t("All times are shown in your local timezone")}
      </p>
    </div>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="flex flex-col items-center px-3 py-1.5">
    <span className="text-[14px] font-bold text-darkBg leading-tight">{value}</span>
    <span className="text-[10px] text-gray-400 font-medium">{label}</span>
  </div>
);

function formatDuration(ms) {
  if (ms < 0) ms = -ms;
  const d = moment.duration(ms);
  const days = Math.floor(d.asDays());
  const h = d.hours();
  const m = d.minutes();
  if (days > 0) return `${days}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(d.asSeconds())}s`;
}

export default PatientJourneyTimeline;
